import { ColumnsCorrespondence } from '@/components/ui/columns-correspondence';
import NoCaseSelected from '@/components/shared/NoCaseSelected';
import { useCaseContext } from '@/components/contexts/CaseContext';
import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Options } from '@/components/ui/fancy-multi-select';
import { backendClient } from '@/lib/api-client';
import { buildDeleteQueryString, getDeleteAttachmentCount } from '@/lib/deleteOptions';
import { toast } from 'react-hot-toast';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { FileUpload, FileUploadHandles } from '@/components/shared/FileUpload';
import { Loading, IsolatedDataTable, TagEditorDialog } from '@/components/shared';
import { ConnectSourcesControls } from '@/components/shared/OutlookSyncProgress';
import { useDocuments, Document } from '@/hooks/useDocuments';
import { useDeadlines } from '@/hooks/useDeadlines';
import { getTimezone } from '@/components/contexts/TimezoneContext';
import { useFolders, type FolderTree } from '@/hooks/useFolders';
import { CorrespondenceFoldersDialog } from '@/components/folders';
import {
  type FolderTableRow,
  buildCorrespondenceFolderTableData,
  buildDeepAnalysisAllowedFolderIds,
  enhanceColumnsForFolderRows,
  getFolderTableHandlers,
} from '@/components/folders/folder-table-utils';
import { FolderCog, FolderTree as FolderTreeIcon, List } from 'lucide-react';
import { Button } from '@/components/ui/NewButton';
import { useSidePanel } from '@/components/contexts/SidePanelContext';
import { filterFilesWithToast } from '@/lib/fileFilterUtils';
import { toolbarActionLabelClass } from '@/lib/toolbar-compact';
import { useBatchCompletionRefresh } from '@/hooks/useBatchCompletionRefresh';
import { useDeepAnalysisAvailability } from '@/hooks/useDeepAnalysisAvailability';
import { ExpandedAttachmentRows, canRowExpand } from '@/components/correspondence/ExpandedAttachmentRows';
import { Row } from '@tanstack/react-table';
import { updateTagColorMap as updateSharedTagColorMap } from '@/components/ui/column-utilities';
import { deriveFilterFields, type FilterField } from '@/components/ui/data-table-filter-toolbar';
import { DEEP_ANALYSIS_LABEL } from '@/hooks/useAnalysisSet';
import { useShellHeader } from '@/components/contexts/ShellHeaderContext';
import {
  isAttachmentsFolder,
  isCorrespondenceFolder,
  isInCorrespondenceFolderTree,
  topLevelCorrespondenceSources,
} from '@/utils/document-utils';
import type { DocumentDeleteOptions } from '@/types/document';
import { mutate as globalMutate } from 'swr';

// Generate a map of tag names to their colors from the database
const generateTagColorMap = (documents: Document[]): Record<string, string> => {
  const tagColorMap: Record<string, string> = {};
  for (const document of documents) {
    for (const tag of document.tags) {
      if (!(tag in tagColorMap) || tagColorMap[tag] === '#CCCCCC') {
        tagColorMap[tag] = document.tag_colors?.[tag] || '#CCCCCC';
      }
    }
  }

  return tagColorMap;
};

let tagColorMap: Record<string, string> = {};
export const getTagColorMap = () => tagColorMap;
export const updateTagColorMap = (data: Document[]) => {
  tagColorMap = generateTagColorMap(data);
};

let uniqueTags: Options[] = [];
export const getUniqueTags = (): Options[] => uniqueTags;
export const updateUniqueTags = (documents: Document[]): void => {
  const tagSet = new Set<string>();

  documents.forEach(document => {
    document.tags.forEach(tag => tagSet.add(tag));
  });

  uniqueTags = Array.from(tagSet).map(tag => ({
    value: tag,
    label: tag,
  }));
};

let uniqueSenders: Options[] = [];
export const getUniqueSenders = (): Options[] => uniqueSenders;
export const updateUniqueSenders = (documents: Document[]): void => {
  const senderSet = new Set<string>();

  documents.forEach(document => {
    document.from.forEach(sender => senderSet.add(sender));
  });

  uniqueSenders = Array.from(senderSet).map(sender => ({
    value: sender,
    label: sender,
  }));
};

let uniqueRecipients: Options[] = [];
export const getUniqueRecipients = (): Options[] => uniqueRecipients;
export const updateUniqueRecipients = (documents: Document[]): void => {
  const recipientSet = new Set<string>();

  documents.forEach(document => {
    document.to.forEach(recipient => recipientSet.add(recipient));
  });

  uniqueRecipients = Array.from(recipientSet).map(recipient => ({
    value: recipient,
    label: recipient,
  }));
};

const Correspondence = () => {
  const { selectedCase } = useCaseContext();
  const [isUploading, setIsUploading] = useState(false);
  const fileUploadRef = useRef<FileUploadHandles>(null);
  useShellHeader({
    header: 'Correspondence',
    subheader: 'A consolidated view of correspondence across your matter.',
  });

  // Target folder for drag and drop
  const [targetFolderId, setTargetFolderId] = useState<string | null>(null);

  // Use the slug from the URL instead of relying on the selectedCase.guid
  const { slug } = useParams({ from: '/matter/$slug/case-files/correspondence' });
  const search = useSearch({ from: '/matter/$slug/case-files/correspondence' });
  const navigate = useNavigate();
  const { isEnabled: deepAnalysisEnabled } = useDeepAnalysisAvailability(slug as string | undefined);
  const deepAnalysisOnly = deepAnalysisEnabled && search.view === 'deep-analysis';
  const setDeepAnalysisOnly = useCallback((nextValue: boolean) => {
    navigate({
      to: '/matter/$slug/case-files/correspondence',
      params: { slug },
      search: nextValue ? { view: 'deep-analysis' } : {},
    });
  }, [navigate, slug]);

  // Side panel for document viewing
  const { openDocumentPanel } = useSidePanel();

  // Folders state
  const [manageCorrespondenceFoldersOpen, setManageCorrespondenceFoldersOpen] = useState(false);
  const [isFlatView, setIsFlatView] = useState(true);
  const { folders, mutate: mutateFolders, isLoading: foldersLoading, error: foldersError } = useFolders(slug as string);
  const timezone = getTimezone();

  // Find the correspondence folder and its subfolders
  const correspondenceFolder = useMemo(() => {
    if (!folders) return null;
    const folder = folders.find(isCorrespondenceFolder);
    return folder;
  }, [folders]);

  // Nested marks are intentionally collapsed here. The backend expands each source to its
  // descendants, so the aggregate remains flat without loading a subtree more than once.
  const correspondenceSourceFolderIds = useMemo(
    () => topLevelCorrespondenceSources(folders).map((folder) => folder.folder_id),
    [folders],
  );
  const folderPathById = useMemo(
    () => {
      const folderById = new Map(folders.map((folder) => [folder.folder_id, folder]));
      const displayPathById = new Map<number, string>();
      const resolving = new Set<number>();

      const resolveDisplayPath = (folderId: number): string => {
        const cached = displayPathById.get(folderId);
        if (cached) return cached;

        const folder = folderById.get(folderId);
        if (!folder) return '';

        // A malformed parent cycle should not make the Correspondence grid unusable.
        if (resolving.has(folderId)) return folder.name;
        resolving.add(folderId);

        const parentPath = typeof folder.parent_folder_id === 'number'
          ? resolveDisplayPath(folder.parent_folder_id)
          : '';
        const displayPath = parentPath ? `${parentPath} / ${folder.name}` : folder.name;

        resolving.delete(folderId);
        displayPathById.set(folderId, displayPath);
        return displayPath;
      };

      for (const folder of folders) resolveDisplayPath(folder.folder_id);
      return displayPathById;
    },
    [folders],
  );
  const correspondenceFolderIds = useMemo(
    () => new Set(
      folders
        .filter((folder) => isInCorrespondenceFolderTree(folder) && !isAttachmentsFolder(folder))
        .map((folder) => folder.folder_id),
    ),
    [folders],
  );
  const correspondenceFoldersByGuid = useMemo(() => new Map(
    folders.map((folder) => [
      folder.guid,
      { ...folder, children: [], document_count: 0, token_count: 0 } satisfies FolderTree,
    ]),
  ), [folders]);

  // Fetch the aggregate once. The folder and flat table presentations are derived locally
  // from this same result, so switching views does not add another document request.
  const { documents: correspondenceData, error, mutate, isLoading: documentsLoading } = useDocuments(
    slug as string,
    undefined,
    undefined,
    undefined,
    {},
    timezone,
    { analysisSetOnly: deepAnalysisOnly, correspondenceOnly: true }
  );
  // Folder rows need source folder IDs. If folders fail, keep the document table so the
  // flat list remains usable.
  const showingFolderRows = !isFlatView && !foldersError;
  const isLoading = documentsLoading || (showingFolderRows && foldersLoading);
  const { deadlines } = useDeadlines(slug as string);
  const deadlineTaskStates = useMemo(
    () => Object.fromEntries(deadlines.map(deadline => [deadline.id, deadline.state])),
    [deadlines]
  );

  // Auto-refresh when a relevant batch completes
  const handleBatchRefresh = useCallback(() => {
    void Promise.all([mutate(), mutateFolders()]);
  }, [mutate, mutateFolders]);

  useBatchCompletionRefresh(
    slug as string,
    correspondenceSourceFolderIds,
    folders,
    handleBatchRefresh
  );

  // Update the shared state when correspondence data changes
  useEffect(() => {
    if (correspondenceData) {
      updateTagColorMap(correspondenceData);
      updateSharedTagColorMap(correspondenceData);
      updateUniqueTags(correspondenceData);
      updateUniqueSenders(correspondenceData);
      updateUniqueRecipients(correspondenceData);
    }
  }, [correspondenceData]);

  // Keep a stable ref to mutate so columns don't recompute on every SWR refresh
  const mutateRef = useRef(mutate);
  useEffect(() => { mutateRef.current = mutate; }, [mutate]);

  // Stable refreshData callback - doesn't change identity when mutate changes
  const refreshData = useCallback(() => { mutateRef.current(); }, []);

  // Keep a stable ref to correspondenceData so handleDelete doesn't need it as a dependency
  const correspondenceDataRef = useRef(correspondenceData);
  useEffect(() => { correspondenceDataRef.current = correspondenceData; }, [correspondenceData]);

  const refreshDeleteCaches = useCallback(async () => {
    if (!slug) return;

    await Promise.allSettled([
      mutateFolders(),
      globalMutate(
        (key: unknown) => typeof key === 'string' && key.startsWith(`/correspondence/${slug}/fetch-all`),
        undefined,
        { revalidate: true }
      ),
    ]);
  }, [slug, mutateFolders]);

  const handleDelete = useCallback(async (selectedIds: string[], options?: DocumentDeleteOptions) => {
    if (!slug || !correspondenceDataRef.current) return;

    const queryString = buildDeleteQueryString(options);

    // Only do optimistic update when we know deletion will proceed (force=true or initial attempt)
    if (options?.force) {
      const updatedData = correspondenceDataRef.current.filter(
        item => !selectedIds.includes(item.id)
      );
      await mutateRef.current(updatedData, false);
    }

    try {
      await backendClient.post(`/correspondence/${slug}/delete-files${queryString}`, selectedIds);
      // If this was the initial (non-force) call that succeeded, do the optimistic update now
      if (!options?.force) {
        const updatedData = correspondenceDataRef.current.filter(
          item => !selectedIds.includes(item.id)
        );
        await mutateRef.current(updatedData, false);
      }
      await refreshDeleteCaches();
    } catch (err: any) {
      // Revert optimistic update if we did one
      if (options?.force) {
        await mutateRef.current();
      }
      throw err;
    }
  }, [slug, refreshDeleteCaches]);

  const handleProcessingComplete = useCallback(() => {
    // Force a revalidation of the data when processing is complete
    mutate();
    setTargetFolderId(null); // Reset target folder after upload
  }, [mutate]);

  const handleDropFiles = useCallback(async (files: File[]) => {
    if (!slug || isUploading) return;

    // Wait for folders to load before allowing file uploads
    if (foldersLoading) {
      console.log('Folders still loading, cannot upload yet');
      toast.error('Please wait for folders to load before uploading files');
      return;
    }

    // Check if correspondence folder exists
    if (!correspondenceFolder) {
      console.log('Correspondence folder not found, folders:', folders?.map(f => ({ name: f.name, path: f.path, id: f.folder_id })));
      toast.error('Correspondence folder not found. Please refresh the page and try again.');
      return;
    }

    // Filter files using shared utility with toast notifications
    const validFiles = filterFilesWithToast(files);
    if (!validFiles) return;

    // Set the target folder ID for the dropped files
    setTargetFolderId(correspondenceFolder.folder_id.toString());

    // Open the FileUpload dialog with filtered files
    if (fileUploadRef.current) {
      fileUploadRef.current.openWithFiles(validFiles);
    }
  }, [slug, isUploading, foldersLoading, correspondenceFolder, folders]);

  const handleFilesDroppedOnFolder = useCallback((files: File[], folderId: number) => {
    const validFiles = filterFilesWithToast(files);
    if (!validFiles) return;

    const folderIdString = folderId.toString();
    setTargetFolderId(folderIdString);
    fileUploadRef.current?.openWithFiles(validFiles, folderIdString);
  }, []);

  const handleEmptyStateClick = () => {
    fileUploadRef.current?.openDialog();
  };

  // Memoize the columns to prevent filter dropdowns from closing on re-render
  const handleFolderRefresh = useCallback(() => {
    void Promise.all([mutateFolders(), mutate()]);
  }, [mutateFolders, mutate]);

  const columns = useMemo(() => {
    const baseColumns = ColumnsCorrespondence({
      refreshData,
      handleDelete: handleDelete,
      slug: slug as string | undefined,
      openDocumentPanel,
      deadlineTaskStates,
      deepAnalysisEnabled,
      deepAnalysisOnly,
      folderPathById,
    });
    return enhanceColumnsForFolderRows(baseColumns, {
      matterSlug: slug as string,
      onRefresh: handleFolderRefresh,
      foldersByGuid: correspondenceFoldersByGuid,
      emptyColumnIds: ['tags', 'from', 'to', 'attachments', 'date'],
      showFolderNameInSummary: true,
      showFolderActions: false,
      navigationContext: 'correspondence',
      deepAnalysisEnabled,
      deepAnalysisOnly,
    });
  }, [
    refreshData,
    handleDelete,
    slug,
    openDocumentPanel,
    deadlineTaskStates,
    deepAnalysisEnabled,
    folderPathById,
    handleFolderRefresh,
    correspondenceFoldersByGuid,
    deepAnalysisOnly,
  ]);

  const allowedFolderIds = useMemo(() => {
    if (!deepAnalysisOnly || foldersLoading) return undefined;
    return buildDeepAnalysisAllowedFolderIds(folders, correspondenceData || []);
  }, [deepAnalysisOnly, foldersLoading, folders, correspondenceData]);

  const tableData = useMemo<FolderTableRow<Document>[]>(() => {
    if (!showingFolderRows) return correspondenceData;

    return buildCorrespondenceFolderTableData(
      folders,
      correspondenceData || [],
      correspondenceFolderIds,
      new Set(correspondenceSourceFolderIds),
      folderPathById,
      allowedFolderIds,
    );
  }, [
    showingFolderRows,
    folders,
    correspondenceData,
    correspondenceFolderIds,
    correspondenceSourceFolderIds,
    folderPathById,
    allowedFolderIds,
  ]);

  const folderTableHandlers = useMemo(() => getFolderTableHandlers<Document>({
    slug: slug as string,
    navigate,
    openDocumentPanel,
    canExpandDocumentRow: canRowExpand,
    deepAnalysisOnly,
    navigationContext: 'correspondence',
  }), [slug, navigate, openDocumentPanel, deepAnalysisOnly]);

  // Config for the Filters menu shown above the table (replaces the old
  // per-column header filters). The column-driven fields are derived from each
  // column's `meta.filter`; page-specific extras are appended below. To vary
  // fields by breakpoint, filter this array with a `useIsMobile()` result.
  const filterFields = useMemo<FilterField[]>(() => {
    const fields: FilterField[] = deriveFilterFields(columns, {
      summary: { placeholder: 'Search subject, summary, filename...' },
    });
    // When Knowledge Graph / Deep Analysis is enabled, fold its view scope
    // (previously a separate toggle) into this single Filters menu.
    if (deepAnalysisEnabled) {
      fields.push({
        id: 'analysisView',
        label: 'View',
        type: 'choice',
        options: [
          { label: 'All files', value: 'all' },
          { label: `${DEEP_ANALYSIS_LABEL} only`, value: 'analysis' },
        ],
        value: deepAnalysisOnly ? 'analysis' : 'all',
        defaultValue: 'all',
        onChange: (value) => setDeepAnalysisOnly(value === 'analysis'),
      });
    }
    return fields;
  }, [columns, deepAnalysisEnabled, deepAnalysisOnly, setDeepAnalysisOnly]);

  // Memoize the sync button to prevent re-renders during sync progress updates
  const syncButton = useMemo(() => {
    return (
      <div className="flex flex-col items-end gap-2">
        <div className="flex gap-2 items-center">
          <ConnectSourcesControls matterId={slug as string} />
          <Button
            variant="outline"
            size="sm"
            onClick={() => setManageCorrespondenceFoldersOpen(true)}
            disabled={foldersLoading || Boolean(foldersError)}
            icon={FolderCog}
            text="Manage Folders"
            textClassName={toolbarActionLabelClass}
            tooltip="Manage Correspondence folders"
            aria-label="Manage Correspondence folders"
          />
          <div className="flex flex-col relative">
            <FileUpload
              ref={fileUploadRef}
              matterGuid={slug as string}
              buttonText="Upload Files"
              onUploadComplete={handleProcessingComplete}
              onProcessingStateChange={setIsUploading}
              showAsDialog={true}
              documentType={targetFolderId || correspondenceFolder?.folder_id?.toString()}
            />
          </div>
        </div>
      </div>
    );
  }, [
    slug,
    handleProcessingComplete,
    correspondenceFolder,
    targetFolderId,
    foldersLoading,
    foldersError,
  ]);

  // Render expanded attachment rows as actual <tr> elements for column alignment.
  const renderSubRows = useCallback((row: Row<FolderTableRow<Document>>) => {
    if (row.original.isFolderRow) return null;
    return (
      <ExpandedAttachmentRows
        row={row as Row<Document>}
        slug={slug as string}
        onDocumentClick={(guid) => openDocumentPanel({ documentGuid: guid, activeTab: 'document' })}
      />
    );
  }, [slug, openDocumentPanel]);

  const documentsTable = correspondenceData && (
    <IsolatedDataTable
      columns={columns}
      data={tableData}
      refreshData={refreshData}
      onRowsSelected={() => {}}
      onDelete={handleDelete}
      getDeleteAttachmentCount={(rows) => getDeleteAttachmentCount(
        rows.filter((row) => !row.isFolderRow) as Document[],
      )}
      isDeleting={false}
      isLoading={isLoading}
      syncButton={syncButton}
      slug={slug as string}
      documentType="Correspondence"
      documentTypeSlug="correspondence"
      enableFilenameSuggestions={false}
      onDropFiles={handleDropFiles}
      onFilesDroppedOnFolder={handleFilesDroppedOnFolder}
      isProcessingDrop={isUploading}
      onEmptyStateClick={handleEmptyStateClick}
      filterFields={filterFields}
      toolbarExtra={(
        <Button
          variant="bare"
          size="sm"
          className="text-slate-600 hover:bg-transparent hover:text-slate-900"
          onClick={() => setIsFlatView((current) => !current)}
          disabled={Boolean(foldersError)}
          icon={showingFolderRows ? List : FolderTreeIcon}
          text={showingFolderRows ? 'Flat view' : 'Folder view'}
          tooltip={foldersError ? 'Folders could not be loaded' : showingFolderRows ? 'Show Flat view' : 'Show Folder view'}
          aria-label={showingFolderRows ? 'Show Flat view' : 'Show Folder view'}
          aria-pressed={showingFolderRows}
        />
      )}
      initialPageSize={100}
      enableBulkTags
      renderSubRows={renderSubRows}
      onAnyRowClick={folderTableHandlers.onAnyRowClick}
      getRowClassName={folderTableHandlers.getRowClassName}
      getRowCanExpand={folderTableHandlers.getRowCanExpand}
      onRowActivate={folderTableHandlers.onRowActivate}
      onRowDoubleClick={folderTableHandlers.onRowDoubleClick}
      deepAnalysisEnabled={deepAnalysisEnabled}
    />
  );

  return (
    <div className="overflow-x-hidden">
      {/* Render the TagEditorDialog at page level */}
      <TagEditorDialog />

      {typeof selectedCase === 'undefined' ? (
        <NoCaseSelected />
      ) : (
        <>
          <div className="px-6 py-6 relative z-20">
            {/* Documents section */}
            <div className="pl-10 lg:pl-0">
              {error ? (
                <div>Error loading correspondence data</div>
              ) : !correspondenceData || (showingFolderRows && foldersLoading) ? (
                <div className="flex flex-col gap-2 justify-center items-center h-full">
                  <Loading />
                  <span className="text-sm font-medium text-gray-700 relative z-10">Loading correspondence...</span>
                </div>
              ) : (
                <div className="pt-2 sm:pt-4 lg:pt-6">
                  {foldersError ? (
                    <div className="mb-3 text-sm text-slate-600">Error loading folders. Showing a flat list.</div>
                  ) : null}
                  {documentsTable}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <CorrespondenceFoldersDialog
        matterSlug={slug as string}
        folders={folders}
        isOpen={manageCorrespondenceFoldersOpen}
        onClose={() => setManageCorrespondenceFoldersOpen(false)}
        onUpdated={async () => {
          await Promise.all([mutateFolders(), mutate()]);
        }}
      />
    </div>
  );
};
export default Correspondence;
